import { HttpApiEndpoint, HttpApiGroup, HttpApiMiddleware, HttpApiSchema, HttpServerRequest, } from "@effect/platform";
import { CurrentOmnitracker } from "@pima/domain-data/principal-context";
import { Context, Effect, Layer } from "effect";
import * as S from "effect/Schema";
import { Unauthorized } from "./principal.js";
/**
 * Group middleware providing CurrentOmnitracker. apps/api sets the header
 * only on a request its session authentication resolved to Omnitracker's
 * service user.
 */
export class OmnitrackerMiddleware extends HttpApiMiddleware.Tag()("@pima/web-api/OmnitrackerMiddleware", {
    provides: CurrentOmnitracker,
    failure: Unauthorized,
}) {
}
/**
 * Internal header the Express bridge sets once the caller is Omnitracker. The
 * bridge builds every header of the synthetic Request itself, so an external
 * caller cannot send one.
 */
export const OMNITRACKER_HEADER = "x-pima-omnitracker";
const OmnitrackerFromJson = S.parseJson(S.Struct({ kind: S.Literal("omnitracker") }));
export const encodeOmnitrackerHeader = S.encodeSync(OmnitrackerFromJson);
const unauthorized = { _tag: "Unauthorized" };
/** Admits a request only when the bridge set the Omnitracker header. */
export const OmnitrackerMiddlewareLive = Layer.succeed(OmnitrackerMiddleware, Effect.gen(function* () {
    const request = yield* HttpServerRequest.HttpServerRequest;
    const raw = request.headers[OMNITRACKER_HEADER];
    if (typeof raw !== "string" || raw.length === 0)
        return yield* Effect.fail(unauthorized);
    return yield* S.decodeUnknown(OmnitrackerFromJson)(raw).pipe(Effect.mapError(() => unauthorized));
}));
/**
 * How this environment treats deliveries. Without it a delivery is judged
 * and recorded in shadow mode with no service area admitted, so nothing is
 * ever written by default.
 */
export class OmnitrackerImportConfig extends Context.Tag("@pima/web-api/OmnitrackerImportConfig")() {
}
export const OmnitrackerRowRejection = S.Struct({
    line: S.Number,
    key: S.NullOr(S.String),
    reason: S.Literal("missing_key", "duplicate_key", "bad_postal_code", "unknown_status", "service_area_not_allowed", "conflicting_location"),
});
export const OmnitrackerImportSummary = S.Struct({
    importId: S.UUID,
    mode: S.Literal("shadow", "apply"),
    outcome: S.Literal("applied", "shrunk", "shadow"),
    rowCount: S.Number,
    placedCount: S.Number,
    unplacedCount: S.Number,
    flaggedCount: S.Number,
    rejections: S.Array(OmnitrackerRowRejection),
});
/** The whole delivery was refused; `reason` says why without quoting rows. */
export const OmnitrackerDeliveryRefused = S.Struct({
    _tag: S.Literal("OmnitrackerPayloadRefused"),
    reason: S.String,
});
/**
 * Omnitracker's hourly push of its full location export. The body is the CSV
 * as sent: decoding it strictly is part of judging the delivery.
 */
export const ImportOmnitrackerLocations = HttpApiEndpoint.post("importLocations", "/omnitracker/locations")
    .setPayload(HttpApiSchema.Uint8Array())
    .addSuccess(S.Struct({ data: OmnitrackerImportSummary }))
    .addError(OmnitrackerDeliveryRefused, { status: 400 });
/** What PIMA One serves Omnitracker; nothing here is reachable by a session. */
export const omnitrackerGroup = HttpApiGroup.make("omnitracker")
    .add(ImportOmnitrackerLocations)
    .middleware(OmnitrackerMiddleware);
//# sourceMappingURL=omnitracker.js.map